import { selectableCellAttrs } from "@/hooks/use-table-cell-selection";
import { getPlainDsdHeaderRowCount } from "@/utils/dsd-editor/cell-label-paths";
import { cn } from "@/lib/utils";
import {
  DsdCell,
  DsdHeaderCell,
  type SelectionColor,
} from "@/toggle-ui-kit/dsd-table";
import { TableScrollArea } from "@/toggle-ui-kit/table-scroll-area";
import {
  dsdTableLayoutOptionsFrom,
  useDsdTableLayout,
} from "@/toggle-ui-kit/use-dsd-table-layout";
import type { DsdNoteReferenceCandidateTone } from "@/utils/dsd-editor/note-reference";
import type {
  PlainDsdCell,
  PlainDsdNoteReferenceStatus,
  PlainDsdTable,
} from "@/types/plain-dsd-table";

import {
  formatDsdEditorSumDifference,
  hasDsdEditorSumDifference,
  isPlainDsdSumCautionCell,
  isPlainDsdSumParentCell,
  parseDsdEditorSumDifference,
} from "@/utils/dsd-editor/sum-validation";
import React, { Fragment, useLayoutEffect, useMemo, useRef } from "react";
import { NOTE_REFERENCE_STATUS_META } from "./note-reference-status-meta";
import {
  NOTE_REFERENCE_CELL_FILL,
  NOTE_REFERENCE_CELL_FILL_DIM,
} from "@/utils/dsd-editor/note-reference-colors";
import { DsdCellText } from "@/utils/dsd-cell-text";

// 표시값의 \n 을 실제 줄바꿈으로 렌더 (줄바꿈 정리는 DsdCellText.display 가 한다)
const renderCellText = (text: string): React.ReactNode => {
  if (!text.includes("\n")) return text;

  return text.split("\n").map((line, index) => (
    <Fragment key={index}>
      {index > 0 && <br />}
      {line}
    </Fragment>
  ));
};

/** 셀 원문을 화면에 그린다 — 들여쓰기(앞 공백)와 셀 안 줄바꿈을 살린다 */
export const renderPlainDsdCellText = (cell: PlainDsdCell): React.ReactNode =>
  renderCellText(DsdCellText.of(cell.rawValue).display);

// 백엔드 to_html() 인라인 배경(`background-color`, 숏핸드 `background` 모두)은 헤더 회색용으로 통과시킨다.
const BACKGROUND_STYLE_PROPS = new Set(["background", "backgroundColor"]);
const PLAIN_DSD_STYLE_PROPS = new Set([
  "textAlign",
  "verticalAlign",
  "width",
  ...BACKGROUND_STYLE_PROPS,
]);

export const buildPlainDsdCellStyle = (
  cell: PlainDsdCell,
  stripBackground = false,
): React.CSSProperties | undefined => {
  const result: Record<string, string> = {};

  if (cell.style) {
    for (const part of cell.style.split(";")) {
      const [key, ...vals] = part.split(":");
      if (!key || vals.length === 0) continue;

      const prop = key.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      if (!PLAIN_DSD_STYLE_PROPS.has(prop)) continue;

      if (stripBackground && BACKGROUND_STYLE_PROPS.has(prop)) {
        continue;
      }
      result[prop] = vals.join(":").trim();
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

// getCellClassName이 배경을 칠하는지 판정. Tailwind bg-* 접두사만 본다 —
// 배경을 주는 커스텀 클래스가 생기면 명시 플래그로 승격할 것.
const hasBackgroundClass = (className: string | undefined) =>
  !!className && /(?:^|\s)bg-/.test(className);

const getCellDataAttributes = (cell: PlainDsdCell) =>
  Object.fromEntries(
    Object.entries(cell.attrs).filter(([key]) => key.startsWith("data-")),
  );

const formatSumDiffLabel = (value: string | undefined) => {
  return (
    formatDsdEditorSumDifference(parseDsdEditorSumDifference(value)) ??
    undefined
  );
};

/** 합계 검증에서 자기 클릭 액션을 가진 셀(합계 부모·불일치) — 일반 셀 선택 대상에서 뺀다. */
export const isSumInteractiveCell = (cell: PlainDsdCell) =>
  isPlainDsdSumParentCell(cell) || isPlainDsdSumCautionCell(cell);

interface PlainDsdCellViewProps {
  cell: PlainDsdCell;
  cornerClassName?: string;
  selected?: boolean;
  /** 다른 셀이 선택돼 이 셀 주석 색을 흐리게 */
  dimNoteReference?: boolean;
  linked?: boolean;
  /** 후보 셀 색상(초록/노랑). 미지정 시 초록 */
  candidateStatus?: DsdNoteReferenceCandidateTone;
  activeCorrection?: boolean;
  highlighted?: boolean;
  sumSelected?: boolean;
  noteReferenceStatus?: PlainDsdNoteReferenceStatus;
  /** 주석 상태 툴팁 문구 */
  noteReferenceLabel?: string;
  getCellDomId?: (cell: PlainDsdCell) => string | undefined;
  /** 셀 배경 등 추가 클래스 — 상태색보다 뒤에 머지되어 우선한다. bg-* 는 인라인 배경도 벗긴다 */
  getCellClassName?: (cell: PlainDsdCell) => string | undefined;
  renderCellValue?: (cell: PlainDsdCell) => React.ReactNode;
  canSelectCell?: (cell: PlainDsdCell) => boolean;
  onCellSelect?: (cellId: string) => void;
  onSumCellSelect?: (cell: PlainDsdCell) => void;
  onCellDoubleClick?: (cell: PlainDsdCell) => void;
  sumValidationEnabled?: boolean;
}

const PlainDsdCellView = ({
  cell,
  cornerClassName,
  selected = false,
  dimNoteReference = false,
  linked = false,
  candidateStatus = "positive",
  activeCorrection = false,
  highlighted = false,
  sumSelected = false,
  noteReferenceStatus,
  noteReferenceLabel,
  getCellDomId,
  getCellClassName,
  renderCellValue,
  canSelectCell,
  onCellSelect,
  onSumCellSelect,
  onCellDoubleClick,
  sumValidationEnabled = false,
}: PlainDsdCellViewProps) => {
  const sumParent = sumValidationEnabled && isPlainDsdSumParentCell(cell);
  const sumCaution = sumValidationEnabled && isPlainDsdSumCautionCell(cell);
  const sumHasDifference =
    sumValidationEnabled &&
    hasDsdEditorSumDifference(
      parseDsdEditorSumDifference(cell.attrs["data-sum-difference"]),
    );
  const sumMismatch = sumCaution || sumHasDifference;
  const sumInteractive = sumValidationEnabled && isSumInteractiveCell(cell);
  const sumDiffLabel = sumMismatch
    ? formatSumDiffLabel(cell.attrs["data-sum-difference"])
    : undefined;
  const displayValue = renderCellValue?.(cell) ?? renderPlainDsdCellText(cell);
  const noteReferenceMeta = noteReferenceStatus
    ? NOTE_REFERENCE_STATUS_META[noteReferenceStatus]
    : null;
  // 셀 색은 주석 상태 하나로 통일 (테두리 없이 배경만 채움)
  const noteReferenceBgClass = !cell.isHeader
    ? dimNoteReference
      ? noteReferenceMeta?.dimBgClassName
      : noteReferenceMeta?.bgClassName
    : undefined;
  // 후보로 잡힌 주석 셀(linked) — 매칭된 소스 상태색(초록/노랑). 다른 셀 선택 시 연하게(dim)
  const linkedBgClass =
    linked && !cell.isHeader
      ? dimNoteReference
        ? NOTE_REFERENCE_CELL_FILL_DIM[candidateStatus]
        : NOTE_REFERENCE_CELL_FILL[candidateStatus]
      : undefined;
  const cellBgClass = noteReferenceBgClass ?? linkedBgClass;
  const extraClassName = getCellClassName?.(cell);
  // 인라인 배경은 클래스 배경을 항상 이긴다 → 클래스로 배경을 칠하는 셀에선 인라인을 벗긴다.
  // 상태색(highlighted/sum/주석)은 DsdCell에만 전달되므로 헤더는 제외한다.
  const stripBackground =
    hasBackgroundClass(extraClassName) ||
    (!cell.isHeader &&
      (highlighted || sumMismatch || sumParent || !!cellBgClass));
  const cellStyle = buildPlainDsdCellStyle(cell, stripBackground);
  const plainSelectionBorderWidth: "thin" | "thick" = linked ? "thin" : "thick";
  const selectable = !!onCellSelect && (canSelectCell?.(cell) ?? true);
  const interactive =
    !cell.isGhost && (selectable || !!onCellDoubleClick || sumInteractive);
  const handleClick = !interactive
    ? undefined
    : (event: React.MouseEvent<HTMLTableCellElement>) => {
        event.stopPropagation();
        if (sumInteractive) onSumCellSelect?.(cell);
        if (selectable) onCellSelect?.(cell.cellId);
      };
  const handleDoubleClick = !interactive
    ? undefined
    : (event: React.MouseEvent<HTMLTableCellElement>) => {
        event.stopPropagation();
        onCellDoubleClick?.(cell);
      };
  const selectionColor: SelectionColor | undefined = sumSelected
    ? sumMismatch
      ? "slate"
      : "orange"
    : undefined;
  const commonProps = {
    ...getCellDataAttributes(cell),
    "data-cell-id": cell.cellId,
    rowSpan: cell.rowspan,
    colSpan: cell.colspan,
    style: cellStyle,
    plainSelectionBorder: selected
      ? // ixd-dsd-compare와 동일하게 border 없이 ring만 (border-0로 오버레이 border-2 덮음)
        "border-0 ring-2 ring-primary-base ring-inset"
      : activeCorrection
        ? "border-blue-600"
        : sumSelected
          ? sumMismatch
            ? "border-slate-500"
            : "border-orange-500"
          : undefined,
    plainSelectionBorderWidth,
    selectionColor,
    selectionLabel: sumSelected
      ? sumMismatch
        ? "합계 불일치"
        : "합계"
      : undefined,
    onClick: handleClick,
    onDoubleClick: handleDoubleClick,
    title:
      noteReferenceLabel ??
      (sumMismatch ? "합계 불일치" : sumParent ? "합계" : undefined),
    className: cn(
      cornerClassName,
      cellBgClass,
      // 색을 50으로 떨군 셀은 폰트도 흐리게
      dimNoteReference && cellBgClass && "text-label-alternative",
      interactive && "cursor-pointer",
      extraClassName,
    ),
  };

  // 드래그 선택/복사 대상 id — 헤더(th)도 본문 셀과 동일하게 대상이다.
  const domId = !cell.isGhost ? getCellDomId?.(cell) : undefined;
  // 복사값은 <br/> 리터럴이 새지 않게 줄바꿈을 정리하고, 엑셀에서 숫자로 읽히게 앞뒤 공백을 뺀다.
  const copyValue = DsdCellText.of(cell.value).clipboard;

  if (cell.isHeader) {
    const scope =
      cell.attrs.scope === "row" || cell.attrs.scope === "col"
        ? cell.attrs.scope
        : undefined;
    return (
      <DsdHeaderCell
        scope={scope}
        ghost={cell.isGhost}
        {...commonProps}
        {...(domId
          ? selectableCellAttrs({
              uniqueId: domId,
              cellId: cell.cellId,
              displayValue: copyValue,
            })
          : {})}
      >
        {displayValue}
      </DsdHeaderCell>
    );
  }

  return (
    <DsdCell
      {...(domId
        ? {
            ...commonProps,
            ...selectableCellAttrs({
              uniqueId: domId,
              cellId: cell.cellId,
              displayValue: copyValue,
            }),
          }
        : {
            ...commonProps,
            // 선택 불가 셀도 표시값 속성은 기존과 동일하게 유지한다
            "data-display-value": copyValue,
          })}
      ghost={cell.isGhost}
      sumStatus={
        sumMismatch ? "sum-mismatch" : sumParent ? "sum-match" : "default"
      }
      sumDiffLabel={sumDiffLabel}
      sumDiffChipVariant="light"
      selected={sumSelected}
      highlighted={highlighted}
    >
      {displayValue}
    </DsdCell>
  );
};

export interface PlainDsdTableViewProps {
  table: PlainDsdTable;
  selectedCellId?: string | null;
  /** 이 셀만 남기고 나머지 주석 색을 흐리게 (미지정 시 selectedCellId 기준) */
  dimExceptCellId?: string | null;
  linkedCellIds?: ReadonlySet<string>;
  /** 선택과 무관하게 항상 하이라이트할 전체 후보 셀 */
  candidateCellIds?: ReadonlySet<string>;
  /** 후보 셀 색 = 매칭된 소스 상태(초록/노랑). 없으면 초록 */
  candidateStatusByCellId?: ReadonlyMap<string, DsdNoteReferenceCandidateTone>;
  activeCorrectionCellId?: string | null;
  noteReferenceStatusByCellId?: ReadonlyMap<
    string,
    PlainDsdNoteReferenceStatus
  >;
  /** 상태 → 셀 툴팁 문구. 문구는 화면이 소유해 넘긴다 */
  noteReferenceLabelByStatus?: Readonly<
    Record<PlainDsdNoteReferenceStatus, string>
  >;
  getCellDomId?: (cell: PlainDsdCell) => string | undefined;
  /** 셀 배경 등 추가 클래스 — 상태색보다 뒤에 머지되어 우선한다. bg-* 는 인라인 배경도 벗긴다 */
  getCellClassName?: (cell: PlainDsdCell) => string | undefined;
  renderCellValue?: (cell: PlainDsdCell) => React.ReactNode;
  canSelectCell?: (cell: PlainDsdCell) => boolean;
  onCellSelect?: (cellId: string) => void;
  onCellDoubleClick?: (cell: PlainDsdCell) => void;
  sumValidationReview?: {
    enabled: boolean;
    selectedCellId: string | null;
    highlightedCellIds: ReadonlySet<string>;
    onSelectCell: (cell: PlainDsdCell) => void;
  };
}

export const PlainDsdTableView = ({
  table,
  selectedCellId = null,
  dimExceptCellId,
  linkedCellIds,
  candidateCellIds,
  candidateStatusByCellId,
  activeCorrectionCellId = null,
  noteReferenceStatusByCellId,
  noteReferenceLabelByStatus,
  getCellDomId,
  getCellClassName,
  renderCellValue,
  canSelectCell,
  onCellSelect,
  onCellDoubleClick,
  sumValidationReview,
}: PlainDsdTableViewProps) => {
  // 흐리게 처리 기준 셀: 호버 미리보기(dimExceptCellId) 우선, 없으면 선택 셀
  const focusCellId =
    dimExceptCellId !== undefined ? dimExceptCellId : selectedCellId;
  // 포커스 셀이 주석 후보인지(=상태 없는 셀). 소스 셀(상태 있음)이 포커스면 후보들을 흐리게 하지 않는다.
  const focusIsNoteCandidate =
    !!focusCellId && !noteReferenceStatusByCellId?.has(focusCellId);
  const layout = useDsdTableLayout(
    table.rows,
    dsdTableLayoutOptionsFrom(table.attrs),
  );
  const headerRowCount = useMemo(
    () => getPlainDsdHeaderRowCount(table),
    [table],
  );
  const tableRef = useRef<HTMLTableElement>(null);
  const adjustedTdsRef = useRef<HTMLElement[]>([]);
  const sumValidationEnabled = sumValidationReview?.enabled ?? false;
  const highlightedSumChildCellIds = useMemo(
    () =>
      sumValidationEnabled
        ? (sumValidationReview?.highlightedCellIds ?? new Set<string>())
        : new Set<string>(),
    [sumValidationEnabled, sumValidationReview?.highlightedCellIds],
  );

  useLayoutEffect(() => {
    const root = tableRef.current;
    if (!root) return;

    adjustedTdsRef.current.forEach((td) =>
      td.style.removeProperty("min-width"),
    );
    adjustedTdsRef.current = [];

    const seen = new Set<HTMLElement>();
    const cells: { td: HTMLElement; num: HTMLElement }[] = [];
    root
      .querySelectorAll<HTMLElement>('[data-chip-type="sum-match"]')
      .forEach((chip) => {
        const td = chip.closest("td");
        if (!(td instanceof HTMLElement) || seen.has(td)) return;

        const num = td.querySelector<HTMLElement>("[data-sum-number]");
        if (!num) return;

        seen.add(td);
        cells.push({ td, num });
      });

    if (cells.length === 0) return;

    void root.offsetWidth;
    cells
      .map(({ td, num }) => ({
        td,
        baseWidth: td.offsetWidth,
        clip: num.scrollWidth - num.clientWidth,
      }))
      .forEach(({ td, baseWidth, clip }) => {
        if (clip > 1) {
          td.style.minWidth = `${baseWidth + clip + 2}px`;
          adjustedTdsRef.current.push(td);
        }
      });
  }, [table, sumValidationEnabled]);

  return (
    <div className="pt-6">
      <TableScrollArea>
        <table ref={tableRef} className={layout.tableClassName}>
          {table.colGroups?.map((colGroup, colGroupIndex) => (
            <colgroup key={colGroupIndex}>
              {colGroup.cols.map((col, colIndex) => (
                <col
                  key={colIndex}
                  style={
                    typeof col.width === "number"
                      ? { width: col.width }
                      : undefined
                  }
                />
              ))}
            </colgroup>
          ))}
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={layout.rowClassName(rowIndex)}
                // 열 머리글 행 표시를 DOM 에 옮긴다 — 근거 창이 이 행을 스크롤 위에 고정한다 (계약 위반 표는 0행)
                data-header-row={rowIndex < headerRowCount || undefined}
              >
                {row.cells.map((cell) => {
                  const isSelected = selectedCellId === cell.cellId;
                  const noteStatus = noteReferenceStatusByCellId?.get(
                    cell.cellId,
                  );
                  // 상태 있는 소스 셀은 제외(기존 dim 동작 보존). 상태 없는 셀 중:
                  // 전체 후보 집합(candidateCellIds) → 선택과 무관하게 항상 초록.
                  // linkedCellIds/선택 셀도 포함(선택 시 linkedCellIds에서 빠지는 것 보완).
                  const isCandidate =
                    !noteReferenceStatusByCellId?.has(cell.cellId) &&
                    (!!candidateCellIds?.has(cell.cellId) ||
                      !!linkedCellIds?.has(cell.cellId));
                  // 후보 셀은 "다른 후보"가 포커스일 때만 흐리게, 소스 포커스면 진하게 유지
                  const dimNote =
                    !!focusCellId &&
                    focusCellId !== cell.cellId &&
                    (isCandidate ? focusIsNoteCandidate : true);
                  return (
                    <PlainDsdCellView
                      key={cell.cellId}
                      cell={cell}
                      cornerClassName={layout.cellClassName(cell.cellId)}
                      selected={isSelected}
                      dimNoteReference={dimNote}
                      linked={isCandidate}
                      candidateStatus={candidateStatusByCellId?.get(
                        cell.cellId,
                      )}
                      activeCorrection={activeCorrectionCellId === cell.cellId}
                      highlighted={highlightedSumChildCellIds.has(cell.cellId)}
                      sumSelected={
                        sumValidationEnabled &&
                        sumValidationReview?.selectedCellId === cell.cellId
                      }
                      noteReferenceStatus={noteStatus}
                      noteReferenceLabel={
                        noteStatus && noteReferenceLabelByStatus?.[noteStatus]
                      }
                      getCellDomId={getCellDomId}
                      getCellClassName={getCellClassName}
                      renderCellValue={renderCellValue}
                      canSelectCell={canSelectCell}
                      onCellSelect={onCellSelect}
                      onSumCellSelect={sumValidationReview?.onSelectCell}
                      onCellDoubleClick={onCellDoubleClick}
                      sumValidationEnabled={sumValidationEnabled}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollArea>
    </div>
  );
};
